import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProcessMenu } from '../components/ProcessMenu.js';
import {
  PROCESS_MENU_OPTIONS,
  makeProcessMenuHandler,
} from './process-menu-shared.js';
import { toast } from 'sonner';
import { api } from '../api/client.js';
import { useCustomer } from '../customer-context.js';
import { resolveActionBlockMessage } from '../lib/action-denied.js';

/**
 * SCRN 51 — Notice Information (View / Apply / Delete) — CCF-90 (view), CCF-91
 * (apply), CCF-92 (delete).
 *
 * Faithful port of the customer's Figma "Notice information" design onto the
 * Accounts-domain notice gateway contract. The agent picks a notice account from
 * the "Notice accounts" dropdown (deposit/notice accounts only, last-4 label per
 * CCF-80); the screen then shows the account header (sort code/account number,
 * balance, required notice period) and the notice table (Notice Number / Amount /
 * Maturity Date) with a Delete action per row (CCF-90/92). "Apply Notice" reveals
 * an amount field + a read-only "This notice will mature on" date and a Submit;
 * applying or deleting refreshes the table (CCF-91/92).
 *
 * Hardcoded sample data from the export is replaced with live data from the
 * Teller-ID-gated BFF via the generated typed client:
 *   - accounts: POST /api/customers/accounts
 *   - notices:  POST /api/accounts/notices
 *   - apply:    POST /api/accounts/notice-apply
 *   - delete:   POST /api/accounts/notice-delete
 *
 * The shared agent banner ("NavDark") lives in the app shell (AppLayout), so this
 * screen renders only the `<main>` content.
 */

/** A single account row (open contract shape; the selector renders a subset). */
interface AccountRow {
  AccountNumber: string;
  AccountType: string;
  Nickname?: string;
  Balance?: number;
  Currency?: string;
  /**
   * Account lifecycle status from `customer/accounts/list` (real wire token,
   * e.g. `ACTIVE` / `CLOSED` / `DORMANT` / `INACTIVE`). Used to keep inactive
   * accounts out of the notice selector (#424).
   */
  Status?: string;
  /**
   * Per-account notice-account indicator (Y/N) — the real wire's
   * `NoticeAccountInd` (CCF-70), the authoritative signal that an account is a
   * NOTICE account (as opposed to a plain savings/term-deposit that shares the
   * `DEPOSIT` bucket). Used to keep non-notice deposit accounts — e.g. a
   * "FlexiSaver Demand" or a "Demand Matured Fix" — out of the selector (#479).
   */
  NoticeAccountInd?: string;
}

/** A single notice row (CCF-90). */
interface NoticeRow {
  NoticeNumber?: string;
  Amount?: number;
  Currency?: string;
  MaturityDate?: string;
}

/** The notice account header + rows (CCF-90). */
interface NoticeList {
  Found?: boolean;
  AccountNumber?: string;
  SortCode?: string;
  Balance?: number;
  Currency?: string;
  NoticeRequired?: string;
  Notices?: NoticeRow[];
  /** Gateway failure reason when Found:false (e.g. 482 non-notice account). */
  ResultCode?: string;
  ResultMessage?: string;
}

/** Maximum notices per account (CCF-91). */
const NOTICE_LIMIT = 3;

/**
 * Notice-amount bounds enforced gateway-side (`NoticeAmount must be between 0.01
 * and 99999999.99`). We mirror them client-side so an out-of-range amount (e.g.
 * `0.00`) is corrected inline before submit rather than bouncing off the gateway
 * (issue #129).
 */
const NOTICE_AMOUNT_MIN = 0.01;
const NOTICE_AMOUNT_MAX = 99999999.99;

/**
 * Format a failed notice action (Apply/Delete) for the INLINE error shown beside
 * the action button (authoritative inline-error UX, @dylan219 #80/#82/#94, and
 * the top-right-toast report on #129): surface the gateway's own Code + Message
 * verbatim (e.g. "483 — Too many notice amounts already added"), falling back to
 * a clear retryable message. A `0000` code is treated as no code. Mirrors
 * BillPaymentsScreen's `formatActionError` (#87).
 */
function formatActionError(
  result: { ResultCode?: string; ResultMessage?: string } | undefined,
  fallback: string,
): string {
  const code = result?.ResultCode?.trim();
  const message = result?.ResultMessage?.trim();
  const showCode = code && code !== '0000';
  if (showCode && message) return `${code} — ${message}`;
  if (message) return message;
  if (showCode) return `${fallback} (code ${code}).`;
  return fallback;
}

/**
 * Formats an amount in its account currency (defaults to EUR). Uses the platform
 * currency formatter so a foreign-currency account renders the correct symbol
 * (USD → $, GBP → £, …), not a hardcoded € (issue #415). The narrow currency
 * symbol is used (USD → `$15,000.00`, not `US$15,000.00`) per issue #536; EUR
 * and GBP are unaffected (still `€`/`£`). Falls back to a code-prefixed string
 * for an unrecognised ISO code.
 */
function formatAmount(amount?: number, currency = 'EUR'): string {
  if (amount === undefined) return '—';
  const code = currency || 'EUR';
  try {
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: code,
      currencyDisplay: 'narrowSymbol',
    }).format(amount);
  } catch {
    return `${code} ${amount.toFixed(2)}`;
  }
}

/**
 * Render the "Sort Code & Account Number" header value (CCF-90, issue #120).
 * The gateway carries no standalone sort code, but the 14-digit PTSB account
 * number is `NSC(6) + AccountNumber(8)`; the BFF derives `SortCode` from it. When
 * present, show the sort code plus the 8-digit account portion (`99-06-04
 * 18923362`) rather than repeating the NSC; otherwise show the raw account
 * number alone (mock 8-digit numbers carry no sort code).
 */
function formatSortCodeAccount(
  sortCode?: string,
  accountNumber?: string,
): string {
  const acct = accountNumber ?? '';
  if (!sortCode) return acct;
  const account = /^\d{14}$/.test(acct) ? acct.slice(6) : acct;
  return `${sortCode} ${account}`;
}

/** Today + `days` as DD/MM/YYYY (the CCF-91 maturity date preview). */
function maturityPreview(days: number): string {
  const d = new Date(Date.now() + days * 24 * 60 * 60 * 1000);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
}

/** Accessible dropdown, faithful to the export's listbox pattern. */
function Dropdown({
  id,
  label,
  value,
  placeholder,
  options,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  placeholder: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    function onClickOutside(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    }
    if (open) {
      document.addEventListener('mousedown', onClickOutside);
      return () => document.removeEventListener('mousedown', onClickOutside);
    }
    return undefined;
  }, [open]);

  return (
    <div className="flex flex-col gap-2" ref={ref}>
      <label
        htmlFor={id}
        className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
      >
        {label}
      </label>
      <div className="relative">
        <button
          id={id}
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
          className="w-full px-4 py-3 border-2 border-[#dad7d3] rounded-md font-['Arial',sans-serif] text-[16px] text-[#1d252c] bg-white text-left transition-all focus:outline-none focus:border-[#005fcc] focus:ring-4 focus:ring-[#005fcc]/20 hover:border-[#979c9f] flex items-center justify-between"
        >
          <span className={selected ? '' : 'text-[#6b7280]'}>
            {selected ? selected.label : placeholder}
          </span>
          <svg
            className={`size-5 transition-transform ${open ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
        {open && (
          <ul
            role="listbox"
            aria-label={label}
            className="absolute top-full mt-2 w-full bg-white border-2 border-[#dad7d3] rounded-md shadow-lg z-[100] max-h-[240px] overflow-y-auto"
          >
            {options.map((option) => (
              <li
                key={option.value || 'placeholder'}
                role="option"
                aria-selected={value === option.value}
                tabIndex={0}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onChange(option.value);
                    setOpen(false);
                  }
                }}
                className={`px-4 py-3 cursor-pointer font-['Arial',sans-serif] text-[16px] text-[#1d252c] hover:bg-[#f3f4f6] focus:outline-none focus:bg-[#e5e7eb] ${
                  value === option.value ? 'bg-[#e5e7eb] font-bold' : ''
                }`}
              >
                {option.label}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export function NoticeInformationScreen(): React.JSX.Element {
  const { customer } = useCustomer();
  const navigate = useNavigate();

  const customerNumber = customer.cif ?? '';
  const tellerId = customer.tellerId ?? '';
  const hasContext = !!customerNumber && /^\d{4}$/.test(tellerId);

  // Shared process-menu navigation handler (issue #45) — wires each option to
  // its existing, RBAC-gated route.
  const onProcessSelect = makeProcessMenuHandler(navigate);

  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [notices, setNotices] = useState<NoticeList | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Apply-notice form state (CCF-91).
  const [showApply, setShowApply] = useState(false);
  const [amount, setAmount] = useState('');
  const [amountError, setAmountError] = useState('');
  // Gateway/apply failure (Code + Message) shown INLINE beside the Submit button
  // — NOT a top-right toast — so the agent sees why it failed and can retry
  // (issue #129; authoritative inline-error UX #80/#82/#94).
  const [submitError, setSubmitError] = useState('');
  // Per-row delete failure, surfaced inline beneath the notices table (sibling
  // action — same inline-error pattern, #129).
  const [deleteError, setDeleteError] = useState('');
  // Surfaced when the agent clicks Apply Notice at the 3-notice limit (CCF-90
  // UP#4: the action is attempted and rejected with an inline error, rather
  // than the button being silently disabled).
  const [limitError, setLimitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Per-row delete in flight (notice number), so its button can disable.
  const [deleting, setDeleting] = useState<string | null>(null);

  // Load the customer's accounts; the selector shows notice/deposit accounts.
  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      if (!hasContext) {
        setError('No verified customer in context. Complete ID&V first.');
        return;
      }
      try {
        const { data, error: apiError } = await api.POST(
          '/customers/accounts',
          {
            params: { header: { 'X-Teller-Id': tellerId } },
            body: { customerNumber },
          },
        );
        if (cancelled) return;
        if (apiError || !data) {
          setError('Could not load accounts.');
          return;
        }
        setAccounts((data.Accounts ?? []) as unknown as AccountRow[]);
      } catch {
        if (!cancelled) setError('Request failed.');
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerNumber, tellerId]);

  // The selector must offer ONLY accounts a notice can actually be applied to
  // (#424/#479, @dylan219): (a) an actual NOTICE account — AND (b) active.
  //
  // Notice-account identity is the real wire's per-account `NoticeAccountInd`
  // (Y/N) flag (CCF-70) — the SAME authoritative signal the Funds-Transfer
  // screen uses to route a force withdrawal. The earlier `AccountType ===
  // 'DEPOSIT'` proxy (#424) was too broad: the `DEPOSIT` bucket also holds plain
  // savings / term-deposit products (ProductType SBA/TDA) that are NOT notice
  // accounts, so accounts like "FlexiSaver Demand" and "Demand Matured Fix"
  // leaked into the dropdown (#479). We now include an account only when it is
  // flagged a notice account (`NoticeAccountInd === 'Y'`, or — as a defensive
  // fallback matching FundsTransferScreen — a nickname that names it a notice
  // account), so non-notice deposit accounts are excluded.
  //
  // Non-active associations (CLOSED / DORMANT / INACTIVE) are still excluded so
  // the agent cannot pick a dead account. Status is compared on the canonical
  // `ACTIVE` token the BFF passes through verbatim from the gateway; a missing
  // Status is treated as active so real rows that omit the field (or the
  // existing `ShowInactive:'N'` active-only feed) are not over-filtered.
  const noticeAccounts = accounts.filter(
    (a) =>
      (a.NoticeAccountInd === 'Y' || /notice/i.test(a.Nickname ?? '')) &&
      (a.Status == null || a.Status === 'ACTIVE'),
  );

  const noticeRows = notices?.Notices ?? [];
  const noticeRequiredDays = Number.parseInt(notices?.NoticeRequired ?? '', 10);
  const atLimit = noticeRows.length >= NOTICE_LIMIT;

  async function loadNotices(accountNumber: string): Promise<void> {
    if (!accountNumber) return;
    setLoading(true);
    setError('');
    setNotices(null);
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/accounts/notices', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: { customerNumber, accountNumber },
      });
      if (apiError || !data) {
        setError(
          response.status === 403
            ? 'Forbidden: check your Teller ID and permissions.'
            : `Could not load notices (${response.status}).`,
        );
        return;
      }
      const result = data as unknown as NoticeList;
      // A gateway "not a notice account / unable to process" outcome
      // (`Found:false`, e.g. ErrorNumber 482 on a non-notice deposit account)
      // must surface as an inline error — NOT a benign empty header with blank
      // Balance/Notice-Required and "No notices on this account", which reads as
      // if the account simply has no notices (issue #120).
      if (result.Found === false) {
        setError(
          result.ResultMessage
            ? `Notices unavailable for this account: ${result.ResultMessage}`
            : 'Notices are not available for the selected account. It may not be a notice account.',
        );
        return;
      }
      setNotices(result);
    } catch {
      setError('Request failed.');
    } finally {
      setLoading(false);
    }
  }

  function onAccountChange(value: string): void {
    setSelectedAccount(value);
    setShowApply(false);
    setAmount('');
    setAmountError('');
    setSubmitError('');
    setDeleteError('');
    // Re-evaluate the 3-notice limit for the newly selected account: the
    // limit error is account-specific, so a stale "Notice limit exceeded"
    // from a previously-selected (full) account must not persist onto an
    // account with fewer notices (issue #416). loadNotices then recomputes
    // `atLimit` from the freshly loaded rows.
    setLimitError('');
    void loadNotices(value);
  }

  async function submitApply(): Promise<void> {
    setSubmitError('');
    const trimmed = amount.trim();
    if (!trimmed) {
      setAmountError('Notice amount is required.');
      return;
    }
    if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) {
      setAmountError('Enter a valid numeric amount.');
      return;
    }
    const value = Number.parseFloat(trimmed);
    // Mirror the gateway bound client-side so `0.00` (and over-max) is corrected
    // inline before submit, not bounced off the gateway as a toast (issue #129).
    if (value < NOTICE_AMOUNT_MIN || value > NOTICE_AMOUNT_MAX) {
      setAmountError(
        `Notice amount must be between €${NOTICE_AMOUNT_MIN.toFixed(2)} and €${NOTICE_AMOUNT_MAX.toFixed(2)}.`,
      );
      return;
    }
    if (notices?.Balance !== undefined && value > notices.Balance) {
      setAmountError('Notice amount exceeds the account balance.');
      return;
    }
    if (atLimit) {
      setAmountError('Notice limit exceeded.');
      return;
    }
    setSubmitting(true);
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/accounts/notice-apply', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: {
          customerNumber,
          accountNumber: selectedAccount,
          amount: trimmed,
        },
      });
      if (
        apiError ||
        !data ||
        (data as { Success?: boolean }).Success !== true
      ) {
        // Surface the failure INLINE beside the Submit button (Code + Message),
        // not as a top-right toast — non-blocking and recoverable so the agent
        // can amend the amount and retry (issue #129; #80/#82/#94). An RBAC
        // *action* denial (403 { error: 'forbidden' }, e.g. an offline agent
        // hitting the online-only write-deny — #248) shows the authoritative
        // verbatim copy; the Teller-ID 403 keeps its own distinct message.
        setSubmitError(
          resolveActionBlockMessage(response.status, apiError) ??
            (response.status === 403
              ? 'Forbidden: check your Teller ID and permissions.'
              : formatActionError(
                  data as
                    | { ResultCode?: string; ResultMessage?: string }
                    | undefined,
                  'Could not apply the notice. Please retry.',
                )),
        );
        return;
      }
      toast.success('Notice applied successfully.');
      setShowApply(false);
      setAmount('');
      setAmountError('');
      setSubmitError('');
      await loadNotices(selectedAccount);
    } catch {
      setSubmitError('Could not apply the notice. Please retry.');
    } finally {
      setSubmitting(false);
    }
  }

  async function deleteNotice(noticeNumber: string): Promise<void> {
    setDeleting(noticeNumber);
    setDeleteError('');
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/accounts/notice-delete', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: { customerNumber, accountNumber: selectedAccount, noticeNumber },
      });
      if (
        apiError ||
        !data ||
        (data as { Success?: boolean }).Success !== true
      ) {
        // Inline beside the notices table (sibling action — same inline-error
        // pattern as Apply), recoverable; not a top-right toast (issue #129).
        // An RBAC *action* denial (403 { error: 'forbidden' } — #248) shows the
        // authoritative verbatim copy; the Teller-ID 403 keeps its own message.
        setDeleteError(
          resolveActionBlockMessage(response.status, apiError) ??
            (response.status === 403
              ? 'Forbidden: check your Teller ID and permissions.'
              : formatActionError(
                  data as
                    | { ResultCode?: string; ResultMessage?: string }
                    | undefined,
                  'Could not delete the notice. Please retry.',
                )),
        );
        return;
      }
      toast.success('Notice deleted successfully.');
      setDeleteError('');
      await loadNotices(selectedAccount);
    } catch {
      setDeleteError('Could not delete the notice. Please retry.');
    } finally {
      setDeleting(null);
    }
  }

  // Currency for the balance/notice amounts (issue #415). The `notice/list` op
  // carries no per-account currency, so take it from the selected account's
  // `customer/accounts/list` record (authoritative, e.g. USD for a US$ Notice
  // Account), falling back to the list envelope then EUR.
  const currency =
    accounts.find((a) => a.AccountNumber === selectedAccount)?.Currency ??
    notices?.Currency ??
    'EUR';

  return (
    <main
      className="max-w-[1800px] mx-auto px-12 py-12"
      aria-labelledby="notice-heading"
    >
      <div className="mb-8">
        <a
          href="/dashboard"
          onClick={(e) => {
            e.preventDefault();
            navigate('/dashboard');
          }}
          className="inline-flex items-center gap-2 text-[16px] font-['Arial',sans-serif] text-[#FF703E] underline hover:text-[#e55a2a] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 rounded"
        >
          &lt; Back to customer dashboard
        </a>
      </div>

      <div className="flex items-center justify-between gap-8 mb-8 flex-wrap">
        <h1
          id="notice-heading"
          className="text-[28px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-8"
        >
          Notice information
        </h1>
        {/* Process-menu navigation (issue #45): jump to a related process.
            Pure navigation chooser — each route stays independently RBAC-gated
            (deny-by-default). */}
        <ProcessMenu
          options={PROCESS_MENU_OPTIONS}
          onSelect={onProcessSelect}
        />
      </div>

      {error && (
        <div
          role="alert"
          className="mb-8 p-4 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c]"
        >
          {error}
        </div>
      )}

      {/* Notice accounts selector (deposit/notice accounts only — CCF-90/80). */}
      <div className="mb-8 w-[400px]">
        <Dropdown
          id="notice-accounts"
          label="Notice accounts"
          placeholder="Select account"
          value={selectedAccount}
          onChange={onAccountChange}
          options={noticeAccounts.map((a) => ({
            value: a.AccountNumber,
            // Lead with the account nickname (e.g. "40 Day Notice", "Deposit")
            // so the agent can tell two deposit accounts apart — a customer can
            // hold several, and a generic "Deposit Account" label made them
            // indistinguishable, inviting selection of a non-notice account
            // (issue #120). Falls back to the generic label when no nickname.
            // No `******` mask prefix before the last 4 — the customer expects the
            // last-4 shown plainly, matching the other account dropdowns (#540).
            label: `${a.Nickname ?? 'Deposit Account'} ${a.AccountNumber.slice(-4)}`,
          }))}
        />
      </div>

      {selectedAccount && (
        <div className="mt-2">
          {loading ? (
            <p
              role="status"
              className="text-[16px] font-['Arial',sans-serif] text-[#6b7280]"
            >
              Loading notices…
            </p>
          ) : notices ? (
            <>
              {/* Account header (CCF-90). */}
              <div className="mb-8 max-w-[600px]">
                <h3 className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-3">
                  Account details
                </h3>
                <table className="w-full border border-[#dad7d3]">
                  <tbody>
                    <tr className="border-b border-[#dad7d3]">
                      <th
                        scope="row"
                        className="px-3 py-2 text-left text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] bg-[#f8f9fa] w-[40%]"
                      >
                        Sort Code &amp; Account Number
                      </th>
                      <td className="px-3 py-2 text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
                        {formatSortCodeAccount(
                          notices.SortCode,
                          notices.AccountNumber,
                        )}
                      </td>
                    </tr>
                    <tr className="border-b border-[#dad7d3]">
                      <th
                        scope="row"
                        className="px-3 py-2 text-left text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] bg-[#f8f9fa]"
                      >
                        Balance
                      </th>
                      <td className="px-3 py-2 text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
                        {formatAmount(notices.Balance, currency)}
                      </td>
                    </tr>
                    <tr>
                      <th
                        scope="row"
                        className="px-3 py-2 text-left text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] bg-[#f8f9fa]"
                      >
                        Notice Required
                      </th>
                      <td className="px-3 py-2 text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
                        {notices.NoticeRequired
                          ? `${notices.NoticeRequired} days`
                          : '—'}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Notices table (CCF-90) with a Delete per row (CCF-92). */}
              <div className="mb-8 overflow-x-auto max-w-[700px]">
                <table className="w-full border-collapse bg-white" role="table">
                  <thead>
                    <tr className="bg-[#f3f4f6]">
                      {[
                        'Notice Number',
                        'Amount',
                        'Maturity Date',
                        'Action',
                      ].map((h) => (
                        <th
                          key={h}
                          className="px-4 py-3 text-left text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] border-b border-[#d1d5db]"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {noticeRows.length > 0 ? (
                      noticeRows.map((n) => (
                        <tr
                          key={n.NoticeNumber}
                          className="border-b border-[#e5e7eb] hover:bg-[#f9fafb]"
                        >
                          <td className="px-4 py-3 text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
                            {n.NoticeNumber}
                          </td>
                          <td className="px-4 py-3 text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
                            {/* Prefer the selected account's currency over any
                                per-row currency: the `notice/list` op carries no
                                per-notice currency, and the balance above is
                                already rendered in the account currency, so the
                                LIST rows must match it (issue #415 reopen — the
                                list showed € on a US$ account). */}
                            {formatAmount(n.Amount, currency)}
                          </td>
                          <td className="px-4 py-3 text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
                            {n.MaturityDate}
                          </td>
                          <td className="px-4 py-3">
                            <button
                              type="button"
                              disabled={deleting === n.NoticeNumber}
                              onClick={() =>
                                n.NoticeNumber &&
                                void deleteNotice(n.NoticeNumber)
                              }
                              aria-label={`Delete notice ${n.NoticeNumber}`}
                              className="px-4 py-2 min-h-[44px] bg-[#FF703E] text-[#1D252C] rounded-lg text-[14px] font-['Arial',sans-serif] font-bold hover:bg-[#e55a2a] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 transition-all whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {deleting === n.NoticeNumber
                                ? 'Working…'
                                : 'Delete'}
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={4}
                          className="px-4 py-3 text-[14px] font-['Arial',sans-serif] text-[#6b7280]"
                        >
                          No notices on this account.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Delete failure surfaced INLINE beneath the notices table
                  (Code + Message), recoverable — not a top-right toast
                  (issue #129). */}
              {deleteError && (
                <p
                  role="alert"
                  className="mb-6 text-[14px] font-['Arial',sans-serif] text-[#dc2626] m-0"
                >
                  {deleteError}
                </p>
              )}

              {/* Apply Notice (CCF-91). At the 3-notice limit the action is
                  attempted and rejected with an inline error (CCF-90 UP#4),
                  rather than the button being disabled. */}
              {!showApply && (
                <button
                  type="button"
                  onClick={() => {
                    if (atLimit) {
                      setLimitError('Notice limit exceeded.');
                      return;
                    }
                    setLimitError('');
                    setShowApply(true);
                    setAmount('');
                    setAmountError('');
                    setSubmitError('');
                  }}
                  className="px-6 py-3 bg-[#FF703E] text-[#1D252C] rounded-lg text-[16px] font-['Arial',sans-serif] font-bold hover:bg-[#e55a2a] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Apply Notice
                </button>
              )}
              {limitError && !showApply && (
                <p
                  role="alert"
                  className="mt-3 text-[14px] font-['Arial',sans-serif] text-[#dc2626] underline m-0"
                >
                  {limitError} This account already has {NOTICE_LIMIT} notices.
                </p>
              )}

              {showApply && (
                <div className="mt-4 max-w-[600px] p-6 border-2 border-[#dad7d3] bg-white">
                  <div className="flex flex-wrap items-start gap-6 mb-2">
                    <div className="w-[240px]">
                      <label
                        htmlFor="notice-amount"
                        className="block text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-2"
                      >
                        Please enter notice amount
                      </label>
                      <input
                        id="notice-amount"
                        type="text"
                        value={amount}
                        onChange={(e) => {
                          setAmount(e.target.value);
                          setAmountError('');
                          setSubmitError('');
                        }}
                        inputMode="decimal"
                        aria-invalid={!!amountError}
                        className={`w-full px-4 py-2 border rounded text-[14px] font-['Arial',sans-serif] text-[#1d252c] placeholder:text-[#9ca3af] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:border-[#005fcc] ${
                          amountError ? 'border-[#dc2626]' : 'border-[#979c9f]'
                        }`}
                      />
                      <div className="min-h-[20px]">
                        {amountError && (
                          <p
                            className="text-[14px] font-['Arial',sans-serif] text-[#dc2626] m-0"
                            role="alert"
                          >
                            {amountError}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="w-[240px]">
                      <label
                        htmlFor="notice-maturity"
                        className="block text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-2"
                      >
                        This notice will mature on
                      </label>
                      <input
                        id="notice-maturity"
                        type="text"
                        readOnly
                        value={
                          Number.isNaN(noticeRequiredDays)
                            ? ''
                            : maturityPreview(noticeRequiredDays)
                        }
                        aria-label="This notice will mature on"
                        className="w-full px-4 py-2 border border-[#979c9f] rounded text-[14px] font-['Arial',sans-serif] text-[#6b7280] bg-[#f3f4f6]"
                      />
                    </div>
                  </div>

                  <div className="flex gap-4 mt-2">
                    <button
                      type="button"
                      disabled={submitting}
                      onClick={() => void submitApply()}
                      className="px-6 py-2.5 bg-[#FF703E] text-[#1D252C] rounded-lg text-[14px] font-['Arial',sans-serif] font-bold hover:bg-[#e55a2a] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitting ? 'Working…' : 'Submit'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowApply(false);
                        setAmount('');
                        setAmountError('');
                        setSubmitError('');
                      }}
                      className="px-6 py-2.5 bg-white text-[#1D252C] border-2 border-[#1D252C] rounded-lg text-[14px] font-['Arial',sans-serif] font-bold hover:bg-[#f5f5f5] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 transition-all"
                    >
                      Cancel
                    </button>
                  </div>
                  {/* Apply failure surfaced INLINE beside the Submit button
                      (Code + Message), recoverable — not a top-right toast
                      (issue #129). */}
                  {submitError && (
                    <p
                      role="alert"
                      className="mt-3 text-[14px] font-['Arial',sans-serif] text-[#dc2626] m-0"
                    >
                      {submitError}
                    </p>
                  )}
                </div>
              )}
            </>
          ) : null}
        </div>
      )}
    </main>
  );
}
